import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { CatalogGeoFacetOptionSchema, CatalogGeoFacetsSchema } from './geo.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Activity master (in-house DB catalog of bookable units).
 *
 * Per the legacy data shape, this single table covers Activity, Transfer,
 * Hotel Transfer, Visa, Meals, Ferry, Tour and Entry Ticket lines - the
 * `category` field discriminates. The B5 itinerary component pickers query
 * Activity filtered by category:
 *   ACTIVITY  -> category = 'Activity'
 *   TRANSFER  -> category IN ('Transfer', 'Hotel Transfer')
 *   VAS       -> category IN ('Visa', 'Meals', 'Entry Ticket only')
 *
 * BigInt paise columns are serialised to numeric strings on the wire
 * (Swagger / JSON-Schema can't represent BigInt).
 */

export const ActivityStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type ActivityStatus = z.infer<typeof ActivityStatusSchema>;

/** Typed channel (replaces deprecated `channel` string column). */
export const ActivityChannelSchema = z.enum([
  'PRIVATE',
  'JOIN_IN_GROUP',
  'SHARED',
  'SCHEDULED',
  'ON_DEMAND',
]);
export type ActivityChannel = z.infer<typeof ActivityChannelSchema>;

/** Typed time-of-day (replaces deprecated `timeOfDay` string column). */
export const ActivityTimeOfDaySchema = z.enum([
  'EARLY_MORNING',
  'MORNING',
  'AFTERNOON',
  'EVENING',
  'NIGHT',
  'FULL_DAY',
]);
export type ActivityTimeOfDay = z.infer<typeof ActivityTimeOfDaySchema>;

/** Weekday (replaces deprecated `runOnDays` CSV string column). */
export const ActivityWeekdaySchema = z.enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
export type ActivityWeekday = z.infer<typeof ActivityWeekdaySchema>;

/** Whether a variant's bundled transfer is shared (SIC) or private. */
export const ActivityTransferTypeSchema = z.enum(['SIC', 'PRIVATE']);
export type ActivityTransferType = z.infer<typeof ActivityTransferTypeSchema>;

/** Which legs a variant's bundled transfer covers. */
export const ActivityTransferDirectionSchema = z.enum(['PICKUP', 'DROPOFF', 'BOTH']);
export type ActivityTransferDirection = z.infer<typeof ActivityTransferDirectionSchema>;

/**
 * Ticket tier (response shape). Exactly one variant per activity is the base
 * (the mandatory "Entry Ticket"). Prices are per-pax in the activity currency.
 */
export const ActivityVariantSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /** Per-variant plain-text one-liner (moved off the Activity master). */
  shortDescription: z.string().nullable(),
  /** Per-variant multi-paragraph plain-text blurb. */
  longDescription: z.string().nullable(),
  adultPrice: z.number(),
  childPrice: z.number(),
  /** Per-variant pax window. Null = no gate. */
  minPax: z.number().int().nullable(),
  maxPax: z.number().int().nullable(),
  /** Legacy `FixedCost`: when > 0, the variant is priced flat regardless of pax
   *  count inside [minPax, maxPax] instead of `adult*A + child*C`. */
  fixedCost: z.number().nullable(),
  isBase: z.boolean(),
  transferIncluded: z.boolean(),
  transferType: ActivityTransferTypeSchema.nullable(),
  transferDirection: ActivityTransferDirectionSchema.nullable(),
  sortOrder: z.number().int(),
});
export type ActivityVariant = z.infer<typeof ActivityVariantSchema>;

export const ActivityListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyCode: z.string().nullable(),
  slug: z.string().nullable(),
  status: ActivityStatusSchema,
  name: z.string(),
  category: z.string(),
  subCategory: z.string().nullable(),
  cityId: UuidSchema.nullable(),
  cityName: z.string(),
  countryName: z.string(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  /** Base variant (Entry Ticket) adult price, in the row's currency. */
  baseAdultPrice: z.number().nullable(),
  /**
   * Same base-adult price, bulk-converted to INR (whole rupees) using the live
   * FX rate at list-time. Null when the base variant has no price. Cached
   * upstream so it's effectively free after the first request per currency.
   */
  baseAdultPriceInr: z.number().nullable().default(null),
  variantCount: z.number().int(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  imageUrl: z.string().nullable(),
  /** Entries in the legacy `photos` array - the cover lives here at sortOrder 0. */
  photoCount: z.number().int(),
  /**
   * Entries in the standalone 4-image gallery (`galleryImageMediaIds`), which
   * is stored separately from `photos`. Listed apart from `photoCount` because
   * the two are different collections; add them for "how many images does this
   * row actually have".
   */
  galleryCount: z.number().int().default(0),
  isApprovedByAdmin: z.boolean(),
  isApprovedByModerator: z.boolean(),
  rating: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type ActivityListItem = z.infer<typeof ActivityListItemSchema>;

export const ActivityListQuerySchema = PaginationQuerySchema.extend({
  status: ActivityStatusSchema.optional(),
  category: z.string().optional(),
  /** Convenience filter for the B5 component pickers. */
  categoryIn: z
    .string()
    .optional()
    .describe('Comma-separated category list; matches any. e.g. "Transfer,Hotel Transfer"'),
  cityId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  /** When true, only activities with at least one photo. */
  hasPhotos: BooleanQueryParamSchema.optional(),
  /**
   * Readiness filter, images half. Counts `photos` AND the standalone gallery
   * together - the same total the list Readiness chip shows - so "Without
   * images" returns exactly the rows badged "No images". `hasPhotos` is the
   * older, photos-only flag kept for the itinerary pickers.
   */
  hasImages: BooleanQueryParamSchema.optional(),
  /**
   * Readiness filter, fields half. `true` = rows the Readiness chip badges as
   * "N missing" (no priced variant); `false` = rows badged "Info complete".
   * Images are the other, independent half - filter those with `hasImages`;
   * the two combine.
   */
  missingInfo: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
  /**
   * When true AND cityId is set, widen the city-scope to every activity in
   * that city's country. The day-picker sends this so an itinerary built for
   * a country whose inventory lives in one city (e.g. Bali all-in-Denpasar)
   * can still surface its activities on days set to sibling cities. Ignored
   * when cityId is absent.
   */
  expandToCountry: BooleanQueryParamSchema.optional(),
});
export type ActivityListQuery = z.infer<typeof ActivityListQuerySchema>;

export const ActivityListResponseSchema = paginatedResponseSchema(ActivityListItemSchema);
export type ActivityListResponse = z.infer<typeof ActivityListResponseSchema>;

/**
 * Facet options for the Activities list filters. Only the countries and cities
 * actually referenced by (non-deleted) activities - so the Country / City
 * filter dropdowns show real, in-use values rather than the whole geography
 * master, and grow automatically as activities for new places are added.
 */
export const ActivityFacetOptionSchema = CatalogGeoFacetOptionSchema;
export type ActivityFacetOption = z.infer<typeof ActivityFacetOptionSchema>;

export const ActivityFacetsSchema = CatalogGeoFacetsSchema;
export type ActivityFacets = z.infer<typeof ActivityFacetsSchema>;

export const ActivityDetailSchema = ActivityListItemSchema.extend({
  /**
   * True when this record holds a captured edit waiting on an admin decision.
   * After an activity's first approval a non-admin's edits go into
   * `pendingPayload` and the live columns are left alone - so a save that
   * looks like it did nothing actually went to the approval queue. The UI
   * needs this to say so instead of reporting a plain "updated".
   */
  awaitingApproval: z.boolean().default(false),
  /** When the captured edit was submitted; null when nothing is pending. */
  pendingSubmittedAt: IsoDateTimeSchema.nullable().default(null),
  countryId: UuidSchema.nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  inclusionsHtml: z.string().nullable(),
  exclusionsHtml: z.string().nullable(),
  termsHtml: z.string().nullable(),
  safetyHtml: z.string().nullable(),
  cancellationHtml: z.string().nullable(),
  // B4.10 typed fields (replaced the deprecated freeflow strings)
  timeOfDayEnum: z.array(ActivityTimeOfDaySchema),
  languageId: UuidSchema.nullable(),
  languageName: z.string().nullable(),
  // Scheduling clock-times (legacy free-text "HH:MM"); end derived from start + duration.
  pickupTime: z.string().nullable(),
  scheduleStartTime: z.string().nullable(),
  scheduleEndTime: z.string().nullable(),
  // Pickup/drop-off as legacy free-text (no location master).
  pickupPoint: z.string().nullable(),
  dropOffPoint: z.string().nullable(),
  runOnWeekdays: z.array(ActivityWeekdaySchema),
  minimumEligibleAge: z.number().int(),
  maximumEligibleAge: z.number().int(),
  tourDurationMinutes: z.number().int(),
  // Ticket tiers (>=1, exactly one isBase).
  variants: z.array(ActivityVariantSchema),
  // Activity-level transfer pricing a variant opts into.
  sicAdultPrice: z.number().nullable(),
  sicChildPrice: z.number().nullable(),
  privateUpto4: z.number().nullable(),
  privateUpto7: z.number().nullable(),
  privateUpto12: z.number().nullable(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable(),
  markupValue: z.number(),
  validityFrom: z.string().nullable(),
  validityTo: z.string().nullable(),
  travelStartDate: z.string().nullable(),
  travelEndDate: z.string().nullable(),
  photos: z.unknown().nullable(),
  /// Standalone gallery of up to 4 ordered media-asset ids (admin-only).
  galleryImageMediaIds: z.array(UuidSchema),
  metaTagName: z.string().nullable(),
  additionalDetails: z.string().nullable(),
  lastAdminApprovedAt: IsoDateTimeSchema.nullable(),
  lastModeratorApprovedAt: IsoDateTimeSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type ActivityDetail = z.infer<typeof ActivityDetailSchema>;

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

/**
 * Ticket tier (request shape). `id` is present when editing an existing
 * variant. Exactly one variant should carry `isBase: true` (the mandatory
 * entry ticket); the API enforces/repairs this.
 */
export const ActivityVariantInputSchema = z.object({
  id: UuidSchema.optional(),
  name: z.string().trim().min(1, 'Variant name is required').max(120),
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescription: z.string().max(50_000).nullable().optional(),
  adultPrice: PriceSchema,
  childPrice: PriceSchema,
  minPax: z.number().int().min(1).nullable().optional(),
  maxPax: z.number().int().min(1).nullable().optional(),
  fixedCost: PriceSchema.nullable().optional(),
  isBase: z.boolean().optional(),
  transferIncluded: z.boolean().optional(),
  transferType: ActivityTransferTypeSchema.nullable().optional(),
  transferDirection: ActivityTransferDirectionSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type ActivityVariantInput = z.infer<typeof ActivityVariantInputSchema>;

/** Photo gallery entry (matches Hotel.photos shape for consistency). */
export const ActivityPhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url().optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), { message: 'Photo needs a mediaId or a url' });
export type ActivityPhoto = z.infer<typeof ActivityPhotoSchema>;
export const ActivityPhotoListSchema = z.array(ActivityPhotoSchema);

const ActivityCoreFieldsSchema = z.object({
  status: ActivityStatusSchema.optional(),
  legacyCode: z.string().trim().max(40).nullable().optional(),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  // Identity
  name: z.string().trim().min(1, 'Name is required').max(300),
  // `category` is hidden in the UI now (every activity is an entry ticket);
  // defaults to 'Activity' server-side. Kept for itinerary markup/picker.
  category: z.string().trim().max(80).optional(),
  subCategory: z.string().trim().max(120).nullable().optional(),
  // Location - city/country chosen via the FK pickers; the name snapshots are
  // filled server-side from the selected ids.
  cityId: UuidSchema.nullable().optional(),
  cityName: z.string().trim().max(120).optional(),
  countryId: UuidSchema.nullable().optional(),
  countryName: z.string().trim().max(120).optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  inclusionsHtml: z.string().max(20_000).nullable().optional(),
  exclusionsHtml: z.string().max(20_000).nullable().optional(),
  termsHtml: z.string().max(20_000).nullable().optional(),
  safetyHtml: z.string().max(20_000).nullable().optional(),
  cancellationHtml: z.string().max(20_000).nullable().optional(),
  // Logistics
  vendorSupplierId: UuidSchema.nullable().optional(),
  // B4.10 typed enums + FKs (Location / Language pickers wired in B4.11)
  timeOfDayEnum: z.array(ActivityTimeOfDaySchema).optional(),
  languageId: UuidSchema.nullable().optional(),
  // Scheduling clock-times ("HH:MM") + free-text pickup/drop-off points.
  pickupTime: z.string().max(20).nullable().optional(),
  scheduleStartTime: z.string().max(20).nullable().optional(),
  scheduleEndTime: z.string().max(20).nullable().optional(),
  pickupPoint: z.string().max(255).nullable().optional(),
  dropOffPoint: z.string().max(255).nullable().optional(),
  runOnWeekdays: z.array(ActivityWeekdaySchema).optional(),
  // Age gate (pax gate moved to ActivityVariant.minPax/maxPax).
  minimumEligibleAge: z.number().int().min(0).max(120).optional(),
  maximumEligibleAge: z.number().int().min(0).max(120).optional(),
  tourDurationMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 30)
    .optional(),
  // Pricing - plain decimal amounts in the row's currency.
  currencyId: UuidSchema.nullable().optional(),
  // Ticket tiers (>=1; exactly one base). Optional on PATCH (omit = unchanged).
  variants: z.array(ActivityVariantInputSchema).min(1).optional(),
  // Activity-level transfer pricing a variant opts into.
  sicAdultPrice: PriceSchema.nullable().optional(),
  sicChildPrice: PriceSchema.nullable().optional(),
  privateUpto4: PriceSchema.nullable().optional(),
  privateUpto7: PriceSchema.nullable().optional(),
  privateUpto12: PriceSchema.nullable().optional(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable().optional(),
  markupValue: PriceSchema.optional(),
  // Validity
  validityFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validityTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  travelStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  travelEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Media
  imageUrl: z.string().trim().url().max(1000).nullable().optional(),
  photos: ActivityPhotoListSchema.nullable().optional(),
  /// Standalone gallery: up to 4 ordered media-asset ids (admin-only).
  /// Kept separate from `photos`. Omit to leave unchanged; `[]` clears.
  galleryImageMediaIds: z.array(UuidSchema).max(4).optional(),
  // Misc
  rating: z.number().int().min(0).max(5).optional(),
  metaTagName: z.string().trim().max(200).nullable().optional(),
  additionalDetails: z.string().max(5000).nullable().optional(),
});

export const CreateActivityRequestSchema = ActivityCoreFieldsSchema;
export type CreateActivityRequest = z.infer<typeof CreateActivityRequestSchema>;

export const UpdateActivityRequestSchema = ActivityCoreFieldsSchema.partial();
export type UpdateActivityRequest = z.infer<typeof UpdateActivityRequestSchema>;

/**
 * Approval action. `tier` selects admin or moderator approval (legacy had
 * both signoff lanes). Admin approval is the gate to status=ACTIVE.
 */
export const ActivityApprovalRequestSchema = z.object({
  tier: z.enum(['admin', 'moderator']),
  approve: z.boolean(),
});
export type ActivityApprovalRequest = z.infer<typeof ActivityApprovalRequestSchema>;
